import { observer } from 'mobx-react-lite'
import { UnlinkDocumentDialog } from '.'
import type { UnlinkDocumentDialogViewModel } from '@/features/docs/components/UnlinkDocumentDialog/UnlinkDocumentDialogViewModel'
import { ModalDialogDescriptionParagraph } from '@/components/ModalDialog'
import { match } from '@/utils/pattern-match-util'

export interface UnlinkDocumentDialogContainerProps {
  /**
   * View model for the unlink document dialog.
   */
  vm: UnlinkDocumentDialogViewModel
}

/**
 * Container component for the unlink document dialog.
 */
export const UnlinkDocumentDialogContainer = observer(
  function UnlinkDocumentDialogContainer({
    vm,
  }: UnlinkDocumentDialogContainerProps) {
    // If the document is null, use a fallback title
    const documentTitle = vm.document?.title ?? 'this document'

    const content = match(vm.accessDetails, 'type', {
      Job: (details) => {
        const title = `Remove from ${details.job.title}?`
        const description = (
          <>
            <ModalDialogDescriptionParagraph>
              Are you sure you want to remove{' '}
              <span className="text-strong">"{documentTitle}"</span> from job{' '}
              <span className="text-strong">"{details.job.title}"</span>?
            </ModalDialogDescriptionParagraph>
            <ModalDialogDescriptionParagraph>
              This will keep the document in your library but remove it from
              this job. You can also choose to permanently delete it from
              everywhere.
            </ModalDialogDescriptionParagraph>
          </>
        )
        return {
          title,
          description,
        }
      },
      Unknown: () => ({
        title: 'Unlink document',
        description: (
          <>
            Are you sure you want to remove{' '}
            <span className="text-strong">"{documentTitle}"</span>?
          </>
        ),
        showDelete: false,
      }),
    })

    const testId = 'unlink-document-dialog'

    return (
      <UnlinkDocumentDialog
        title={content.title}
        description={content.description}
        isOpen={vm.modal.showing}
        isUnlinkPending={vm.unlink.pending}
        isDeletePending={vm.delete.pending}
        onClose={vm.cancel}
        onUnlink={vm.unlink}
        onDelete={vm.delete}
        isSkeleton={vm.accessDetails.type === 'Unknown'}
        testId={testId}
        removeButtonTestId={`${testId}-remove-button`}
        deleteButtonTestId={`${testId}-delete-button`}
        cancelButtonTestId={`${testId}-cancel-button`}
      />
    )
  },
)
